import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { MenuCloseReason } from '@angular/material/menu';
import { accountLanguages } from '../../../assets/languages/account-language';
import { AccountScope } from '../../core/account-scope';

export interface AccountEntry {
  readonly id: number;
  readonly name: string;
  /** Account language; the flag is resolved from it here. */
  readonly language?: string;
}

interface DisplayedAccount extends AccountEntry {
  readonly selected: boolean;
  readonly flagClass: string;
  readonly current: boolean;
}

function flagFor(language: string | undefined | null): string {
  if (!language) {
    return '';
  }
  return accountLanguages.find((entry) => entry.code === language)?.flag ?? '';
}

function sameScope(first: AccountScope, second: AccountScope): boolean {
  if (typeof first === 'string' || typeof second === 'string') {
    return first === second;
  }
  const picked = new Set(first);
  return first.length === second.length && second.every((id) => picked.has(id));
}

/**
 * Picks are applied by the Apply button, so choosing three accounts costs one reload, not three.
 */
@Component({
  selector: 'app-account-selector',
  templateUrl: './account-selector.component.html',
  // The flag sprite is not global: every component showing flags pulls it in.
  styleUrls: ['./account-selector.component.scss', '../../../assets/styles/country-flags.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSelectorComponent {
  readonly entries = input.required<readonly AccountEntry[]>();

  readonly currentSiteId = input<number | null | undefined>(undefined);

  readonly selection = input<AccountScope>('current');

  readonly applied = output<AccountScope>();

  private readonly draft = signal<AccountScope>('current');
  private dirty = false;

  constructor() {
    // The trigger has to show what the page is actually filtered to. Once the user starts editing,
    // the draft owns itself until it is applied.
    effect(() => {
      const selection = this.selection();
      if (!this.dirty) {
        this.draft.set(selection);
      }
    });
  }

  private readonly pickedIds = computed<ReadonlySet<number>>(() => {
    const draft = this.draft();
    if (draft === 'all') {
      return new Set();
    }
    if (draft === 'current') {
      const currentSiteId = this.currentSiteId();
      return new Set(currentSiteId == null ? [] : [currentSiteId]);
    }
    return new Set(draft);
  });

  protected readonly displayedAccounts = computed<DisplayedAccount[]>(() => {
    const picked = this.pickedIds();
    const currentSiteId = this.currentSiteId();
    const entries = this.entries();
    const currentFirst = [
      ...entries.filter((entry) => entry.id === currentSiteId),
      ...entries
        .filter((entry) => entry.id !== currentSiteId)
        .sort((first, second) => first.name.localeCompare(second.name, undefined, { sensitivity: 'base' })),
    ];
    return currentFirst.map((entry) => ({
      ...entry,
      selected: picked.has(entry.id),
      flagClass: flagFor(entry.language),
      current: entry.id === currentSiteId,
    }));
  });

  protected readonly currentSelected = computed(() => this.draft() === 'current');

  protected readonly allSelected = computed(() => this.draft() === 'all');

  protected readonly selectedCount = computed(() => this.pickedIds().size);

  private readonly currentAccount = computed<AccountEntry | null>(
    () => this.entries().find((entry) => entry.id === this.currentSiteId()) ?? null,
  );

  protected readonly canApply = computed(() => !sameScope(this.draft(), this.selection()));

  private readonly onlySelected = computed<AccountEntry | null>(() => {
    if (this.currentSelected()) {
      return this.currentAccount();
    }
    const picked = this.pickedIds();
    if (picked.size !== 1) {
      return null;
    }
    return this.entries().find((entry) => picked.has(entry.id)) ?? null;
  });

  protected readonly label = computed(() => this.onlySelected()?.name ?? null);

  protected readonly triggerFlagClass = computed(() => flagFor(this.onlySelected()?.language));

  protected selectAll(): void {
    if (this.draft() !== 'all') {
      this.draft.set('all');
      this.dirty = true;
    }
    this.apply();
  }

  protected toggle(id: number): void {
    const picked = new Set(this.pickedIds());
    if (picked.has(id)) {
      picked.delete(id);
    } else {
      picked.add(id);
    }
    this.draft.set(this.toScope(picked));
    this.dirty = true;
  }

  // Pages hand in 'current', so a pick of only the current account must be 'current' to compare equal to it.
  private toScope(picked: ReadonlySet<number>): AccountScope {
    const currentSiteId = this.currentSiteId();
    const onlyCurrent = picked.size === 1 && currentSiteId != null && picked.has(currentSiteId);
    if (!picked.size || onlyCurrent) {
      return 'current';
    }
    return [...picked];
  }

  protected apply(): void {
    if (!this.dirty) {
      return;
    }
    this.dirty = false;
    this.applied.emit(this.draft());
  }

  // Material closes the menu on Tab before focus can reach the Apply button, so tabbing out has to commit.
  protected menuClosed(reason: MenuCloseReason): void {
    if (reason === 'tab') {
      this.apply();
      return;
    }
    this.discard();
  }

  private discard(): void {
    if (!this.dirty) {
      return;
    }
    this.dirty = false;
    this.draft.set(this.selection());
  }
}
